import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@iconify/react';
import MasterLayout from '../../masterLayout/MasterLayout';
import Breadcrumb from '../../components/Breadcrumb';
import KkpColorBadge from '../../components/kkp/KkpColorBadge';
import KkpClientAutocomplete from '../../components/kkp/KkpClientAutocomplete';
import KkpPlanningStickyTableWrap from '../../components/kkp/KkpPlanningStickyTableWrap';
import { AppFilterBar, AppFilterSelect, AppFilterField } from '../../components/filters';
import SortableTh from '../../components/common/SortableTh';
import KkpVenteBriefModal from '../../components/kkp/KkpVenteBriefModal';
import KkpCreaPubValidationModal from '../../components/kkp/KkpCreaPubValidationModal';
import KkpCreaPubFichiersModal from '../../components/kkp/KkpCreaPubFichiersModal';
import { useTableSort } from '../../hooks/useTableSort';
import kkpService from '../../services/kkpService';
import { getKkpDefaultPeriod } from '../../utils/kkpDefaultPeriod';
import { filterRowsByMulti, singleOrEmpty } from '../../utils/kkpPlanningFilters';
import { setPubGraphisteAFaireCount } from '../../utils/pubGraphisteCountStore';
import { venteBriefFromApi } from '../../utils/kkpVenteBriefUtils';
import { notify } from '../../components/common/AppToast';
import { AppAlert, AppButton, AppConfetti, AppEmptyState, AppPillSwitch } from '../../components/ui';
import { STATUT_BAT_VALIDE, canDeleteSuiviCreaPub, canEditCreaPubFichiers } from '../../utils/kkpBatValide';
import { confirmPubGraphisteDelete } from '../../utils/confirmVenteDelete';
import useAppConfirm from '../../hooks/useAppConfirm';
import '../../styles/kkp-pub-graphiste.scss';
import '../../styles/kkp-planning-page.scss';

const MOIS_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
  value: String(i + 1),
  label: new Date(2000, i, 1).toLocaleString('fr-FR', { month: 'long' }),
}));

const URGENCY_LEGEND = [
  { level: 'ok', label: 'Moins de 1 jour' },
  { level: 'warning', label: '1 à 2 jours' },
  { level: 'warning_strong', label: '2 à 3 jours' },
  { level: 'danger', label: 'Plus de 3 jours' },
];

const TABLE_SORT_COLUMNS = {
  urgence: { type: 'number', getValue: (row) => row.urgency_rank ?? 0 },
  client: { type: 'string', getValue: (row) => row.client_label },
  pub: { type: 'string', getValue: (row) => `${row.canal_label} ${row.format} ${row.contenu}` },
  periode: { type: 'string', getValue: (row) => row.periode_label },
  commercial: { type: 'string', getValue: (row) => row.commercial_nom },
  terminee: { type: 'date', getValue: (row) => row.pub_graphiste_terminee_at },
  statut: { type: 'string', getValue: (row) => row.statut_phase },
};

function UrgencyPastille({ level, label }) {
  return (
    <span
      className={`kkp-pub-graphiste-pastille kkp-pub-graphiste-pastille--${level}`}
      title={label}
      aria-label={label}
    />
  );
}

function computeClientRowspans(rows) {
  return rows.map((row, index) => {
    if (index > 0 && rows[index - 1].client_label === row.client_label) {
      return 0;
    }
    let span = 1;
    for (let j = index + 1; j < rows.length; j += 1) {
      if (rows[j].client_label === row.client_label) {
        span += 1;
      } else {
        break;
      }
    }
    return span;
  });
}

function rowHasBrief(row) {
  const hasText = venteBriefFromApi(row.brief_encart).trim() !== '';
  const hasAttachments = (row.brief_pieces_jointes?.length ?? 0) > 0;
  return hasText || hasAttachments;
}

const KkpPubGraphistePage = () => {
  const defaultPeriod = getKkpDefaultPeriod();
  const canEditCrea = canEditCreaPubFichiers();
  const canDelete = canDeleteSuiviCreaPub();
  const { ask, modal } = useAppConfirm();
  const [activeTab, setActiveTab] = useState('a_faire');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [terminatingId, setTerminatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [validationRow, setValidationRow] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [briefVente, setBriefVente] = useState(null);
  const [creaVente, setCreaVente] = useState(null);
  const [filters, setFilters] = useState({
    annee: [String(defaultPeriod.annee)],
    mois: [String(defaultPeriod.mois)],
    search: '',
  });
  const [searchDraft, setSearchDraft] = useState('');

  useEffect(() => {
    if (activeTab !== 'faites') {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      setFilters((f) => (f.search === searchDraft ? f : { ...f, search: searchDraft }));
    }, 350);
    return () => window.clearTimeout(timer);
  }, [searchDraft, activeTab]);

  const { sortedRows, sort, setSort, toggleSort, getSortIcon } = useTableSort(items, TABLE_SORT_COLUMNS);
  const clientRowspans = useMemo(() => computeClientRowspans(sortedRows), [sortedRows]);

  useEffect(() => {
    if (activeTab !== 'faites') {
      setSort({ key: null, direction: 'asc' });
      return;
    }
    if (!singleOrEmpty(filters.mois)) {
      setSort({ key: 'terminee', direction: 'desc' });
    } else {
      setSort({ key: null, direction: 'asc' });
    }
  }, [activeTab, filters.mois, setSort]);

  const anneeOptions = useMemo(() => {
    const current = new Date().getFullYear();
    return Array.from({ length: 5 }, (_, i) => ({
      value: String(current - 2 + i),
      label: String(current - 2 + i),
    }));
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const anneeSingle = singleOrEmpty(filters.annee);
      const moisSingle = singleOrEmpty(filters.mois);
      const params = activeTab === 'faites'
        ? {
            tab: 'faites',
            annee: anneeSingle ? Number(anneeSingle) : undefined,
            mois: moisSingle ? Number(moisSingle) : undefined,
            search: filters.search || undefined,
          }
        : { tab: 'a_faire' };

      const data = await kkpService.getPubGraphiste(params);
      let rows = data.items ?? [];
      rows = filterRowsByMulti(rows, filters.annee, (row) => row.annee);
      rows = filterRowsByMulti(rows, filters.mois, (row) => row.mois);
      setItems(rows);
      setTotal(rows.length);
      if (activeTab === 'a_faire') {
        setPubGraphisteAFaireCount(Number(data?.total) || rows.length);
      }
    } catch (err) {
      setError(err.message);
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [activeTab, filters.annee, filters.mois, filters.search]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleValidateSubmit = async (files, statutPhase) => {
    if (!validationRow) return;

    setTerminatingId(validationRow.id);
    setValidationError(null);
    try {
      await kkpService.marquerPubGraphisteTerminee(validationRow.id, files, statutPhase);
      setValidationRow(null);
      if (statutPhase === STATUT_BAT_VALIDE) {
        notify.success('Créa validée en BAT Validé. Un e-mail a été envoyé au commercial.');
      } else {
        notify.success('Créa validée en BAT Envoyé.');
      }
      await loadData();
    } catch (err) {
      setValidationError(err.message);
    } finally {
      setTerminatingId(null);
    }
  };

  const handleDelete = async (row) => {
    if (!canDelete || !row?.id) return;
    if (!(await confirmPubGraphisteDelete(ask, row.client_label))) return;

    setDeletingId(row.id);
    try {
      await kkpService.deletePubGraphiste(row.id);
      notify.success('Publicité retirée du suivi créa pub.');
      await loadData();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setDeletingId(null);
    }
  };

  const breadcrumbActions = (
    <button type="button" className="btn btn-sm btn-outline-primary" onClick={loadData} disabled={loading}>
      <Icon icon="mdi:refresh" className="me-1" />
      Actualiser
    </button>
  );

  return (
    <MasterLayout>
      <Breadcrumb
        title="Suivi créa pub"
        items={[
          { label: 'Koikispass', to: '/kkp/planning/mag' },
          { label: 'Chemin de fer MAG V2' },
          { label: 'Suivi créa pub' },
        ]}
        actions={breadcrumbActions}
      />

      <div className="card kkp-pub-graphiste-card">
        <div className="card-header border-bottom-0 pb-0">
          <AppPillSwitch
            className="mb-2"
            ariaLabel="Suivi créa pub"
            value={activeTab}
            onChange={setActiveTab}
            options={[
              {
                value: 'a_faire',
                label: 'À faire',
                icon: 'mdi:clipboard-list-outline',
                count: total > 0 ? total : null,
              },
              {
                value: 'faites',
                label: 'Faites',
                icon: 'mdi:check-circle-outline',
              },
            ]}
          />
        </div>

        <div className="card-body pt-3">
          {activeTab === 'a_faire' && (
            <div className="kkp-pub-graphiste-legend mb-3">
              {URGENCY_LEGEND.map((item) => (
                <span key={item.level} className="kkp-pub-graphiste-legend-item">
                  <UrgencyPastille level={item.level} label={item.label} />
                  {item.label}
                </span>
              ))}
            </div>
          )}

          {activeTab === 'faites' && (
            <AppFilterBar
              className="mb-3"
              onReset={() => {
                setSearchDraft('');
                setFilters({
                  annee: [String(defaultPeriod.annee)],
                  mois: [String(defaultPeriod.mois)],
                  search: '',
                });
              }}
            >
              <AppFilterSelect
                id="pub-annee"
                label="Année"
                value={filters.annee}
                onChange={(annee) => setFilters((f) => ({ ...f, annee }))}
                options={anneeOptions}
                multiple={false}
              />
              <AppFilterSelect
                id="pub-mois"
                label="Mois"
                value={filters.mois}
                onChange={(mois) => setFilters((f) => ({ ...f, mois }))}
                options={MOIS_OPTIONS}
                placeholder="Tous les mois"
                multiple={false}
              />
              <AppFilterField label="Client" col="col-12 col-md-6 col-lg-3">
                <KkpClientAutocomplete
                  value={searchDraft}
                  onChange={setSearchDraft}
                  size="sm"
                  placeholder="Client ou tiers Dolibarr…"
                />
              </AppFilterField>
            </AppFilterBar>
          )}

          {error && <AppAlert>{error}</AppAlert>}

          {loading ? (
            <AppEmptyState loading />
          ) : items.length === 0 && (activeTab !== 'a_faire' || error) ? (
            <AppEmptyState
              message={
                activeTab === 'a_faire'
                  ? 'Aucune publicité en attente de création graphiste.'
                  : 'Aucune publicité terminée pour cette période.'
              }
            />
          ) : (
            <>
              {items.length === 0 && (
                <>
                  <AppConfetti />
                  <div className="app-celebration" role="status">
                    <Icon icon="mdi:check-circle" className="app-celebration__icon" aria-hidden />
                    <p className="app-celebration__title">Bravo, toutes les pubs sont faites !</p>
                    <p className="app-celebration__text">Aucune publicité en attente de création graphiste.</p>
                  </div>
                </>
              )}
            <KkpPlanningStickyTableWrap>
              <table className="table table-hover kkp-planning-table kkp-pub-graphiste-table mb-0">
                <thead>
                  <tr>
                    {activeTab === 'a_faire' && (
                      <SortableTh sortKey="urgence" label="Urgence" className="kkp-pub-graphiste-urgency-cell" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                    )}
                    <SortableTh sortKey="client" label="Client" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                    <SortableTh sortKey="pub" label="Pub" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                    <SortableTh sortKey="periode" label="Période" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                    <SortableTh sortKey="commercial" label="Commercial" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                    {activeTab === 'faites' && (
                      <SortableTh sortKey="terminee" label="Terminée le" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                    )}
                    <SortableTh sortKey="statut" label="Statut" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                    <SortableTh label="Actions" className="text-end" sortable={false} />
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map((row, index) => (
                    <tr
                      key={row.id}
                      className={index % 2 === 0 ? 'kkp-pub-graphiste-row--stripe-odd' : 'kkp-pub-graphiste-row--stripe-even'}
                    >
                      {activeTab === 'a_faire' && (
                        <td className="kkp-pub-graphiste-urgency-cell">
                          <UrgencyPastille
                            level={row.urgency_level}
                            label={row.urgency_label || 'Urgence'}
                          />
                        </td>
                      )}
                      {clientRowspans[index] > 0 && (
                        <td
                          rowSpan={clientRowspans[index]}
                          className="kkp-pub-graphiste-client-cell fw-medium"
                        >
                          {row.client_label}
                        </td>
                      )}
                      <td className="kkp-pub-graphiste-planning-cell">
                        <div className="kkp-pub-graphiste-planning-stack">
                          <span className="badge kkp-pub-graphiste-canal-badge align-self-start">{row.canal_label}</span>
                          <span className="kkp-pub-graphiste-planning-detail fw-medium">{row.format}</span>
                          <span className="kkp-pub-graphiste-planning-detail text-secondary-light">{row.contenu}</span>
                        </div>
                      </td>
                      <td>{row.periode_label}</td>
                      <td>{row.commercial_nom || '—'}</td>
                      {activeTab === 'faites' && (
                        <td>{row.pub_graphiste_terminee_at_label || '—'}</td>
                      )}
                      <td>
                        <KkpColorBadge label={row.statut_phase} color={row.statut_couleur} />
                      </td>
                      <td className="text-end">
                        <div className="d-inline-flex gap-1">
                          <button
                            type="button"
                            className={`btn btn-sm ${rowHasBrief(row) ? 'btn-outline-primary' : 'btn-outline-secondary opacity-50'}`}
                            title={
                              rowHasBrief(row)
                                ? row.brief_pieces_jointes?.length
                                  ? `Consulter le brief (${row.brief_pieces_jointes.length} pièce${row.brief_pieces_jointes.length > 1 ? 's' : ''} jointe${row.brief_pieces_jointes.length > 1 ? 's' : ''})`
                                  : 'Consulter le brief créatif'
                                : 'Aucun brief disponible'
                            }
                            onClick={() => setBriefVente(row)}
                          >
                            <Icon icon="mdi:file-document-outline" />
                          </button>
                          {activeTab === 'faites' && (
                            <button
                              type="button"
                              className={`btn btn-sm ${row.crea_pub_pieces_jointes?.length || canEditCrea ? 'btn-outline-success' : 'btn-outline-secondary opacity-50'}`}
                              title={
                                row.crea_pub_pieces_jointes?.length
                                  ? (canEditCrea
                                    ? `Voir / modifier ${row.crea_pub_pieces_jointes.length} création${row.crea_pub_pieces_jointes.length > 1 ? 's' : ''}`
                                    : `Voir ${row.crea_pub_pieces_jointes.length} création${row.crea_pub_pieces_jointes.length > 1 ? 's' : ''}`)
                                  : (canEditCrea ? 'Ajouter une création' : 'Aucune création déposée')
                              }
                              onClick={() => setCreaVente(row)}
                              disabled={!row.crea_pub_pieces_jointes?.length && !canEditCrea}
                            >
                              <Icon icon="mdi:image-multiple-outline" />
                            </button>
                          )}
                          {activeTab === 'a_faire' && (
                            <AppButton
                              type="button"
                              variant="outline-success"
                              size="sm"
                              title="Valider et déposer la créa"
                              icon="mdi:check"
                              loading={terminatingId === row.id}
                              onClick={() => {
                                setValidationError(null);
                                setValidationRow(row);
                              }}
                            />
                          )}
                          <Link
                            to={row.planning_path}
                            className="btn btn-sm btn-outline-secondary"
                            title="Ouvrir le planning"
                          >
                            <Icon icon="mdi:open-in-new" />
                          </Link>
                          {canDelete && (
                            <AppButton
                              type="button"
                              variant="outline-danger"
                              size="sm"
                              title="Supprimer du suivi créa pub"
                              aria-label="Supprimer du suivi créa pub"
                              icon="mdi:delete"
                              loading={deletingId === row.id}
                              onClick={() => handleDelete(row)}
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </KkpPlanningStickyTableWrap>
            </>
          )}
        </div>
      </div>

      <KkpVenteBriefModal
        show={Boolean(briefVente)}
        vente={briefVente}
        onClose={() => setBriefVente(null)}
        onError={setError}
      />

      <KkpCreaPubValidationModal
        show={Boolean(validationRow)}
        row={validationRow}
        onClose={() => setValidationRow(null)}
        onSubmit={handleValidateSubmit}
        submitting={Boolean(validationRow && terminatingId === validationRow.id)}
        error={validationError}
      />

      <KkpCreaPubFichiersModal
        show={Boolean(creaVente)}
        vente={creaVente}
        onClose={() => setCreaVente(null)}
        onError={setError}
        onChange={(pieces) => {
          const venteId = creaVente?.id;
          setCreaVente((prev) => (prev ? { ...prev, crea_pub_pieces_jointes: pieces } : null));
          if (!venteId) return;
          setItems((prev) => prev.map((row) => (
            row.id === venteId
              ? { ...row, crea_pub_pieces_jointes: pieces }
              : row
          )));
        }}
      />
      {modal}
    </MasterLayout>
  );
};

export default KkpPubGraphistePage;
